/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * The 待服务项目 section: the covered items with their customer fix/decline
 * decision, and (when active) the add-items affordance in the header.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LeadItem } from '@euler/model/lead/LeadItem';
import { TouchableOpacity, View } from 'react-native';
import { LeadItemTypeIcon } from '../LeadItemTypeIcon';
import { Card } from '../common/Card';
import { SectionAction } from '../common/SectionAction';
import { SectionHeader } from '../common/SectionHeader';
import { LD } from '../leadDetailKit';
import { DecisionChip } from './DecisionChip';

export const CoveredItemsSection = ({
  items,
  terminal,
  onAdd,
  onItemAction,
}: {
  items: LeadItem[];
  terminal: boolean;
  onAdd?: () => void;
  onItemAction: (item: LeadItem, canRemove: boolean) => void;
}) => {
  const { rpx } = useResponsiveContext();
  // The backend forbids removing the last active item, so the remove action
  // is offered only when more than one remains.
  const activeItemCount = items.filter(i => i.isActive).length;

  return (
    <>
      <SectionHeader
        title={`待服务项目 (${items.length})`}
        right={
          onAdd ? <SectionAction label="添加项目" onPress={onAdd} /> : undefined
        }
      />
      <Card>
        {items.length === 0 ? (
          <View style={{ paddingVertical: rpx(16), alignItems: 'center' }}>
            <Typography.Text size={rpx(14)} color={LD.mist}>
              暂无待服务项目
            </Typography.Text>
          </View>
        ) : (
          items.map((item, index) => (
            <TouchableOpacity
              key={item.id}
              activeOpacity={terminal ? 1 : 0.6}
              onPress={
                terminal
                  ? undefined
                  : () => onItemAction(item, activeItemCount > 1)
              }
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: rpx(12),
                borderTopWidth: index === 0 ? 0 : 1,
                borderTopColor: LD.hairline,
              }}
            >
              <View
                style={{
                  width: rpx(38),
                  height: rpx(38),
                  borderRadius: rpx(19),
                  backgroundColor: LD.brandSoft,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: rpx(12),
                }}
              >
                <LeadItemTypeIcon
                  type={
                    item.source === 'catalog'
                      ? item.subjectType ?? 'other'
                      : item.type
                  }
                  size={rpx(26)}
                  color={LD.brand}
                />
              </View>
              <View style={{ flex: 1, paddingRight: rpx(10) }}>
                <Typography.Text size={rpx(15)} color={LD.ink} bold>
                  {item.name}
                </Typography.Text>
                {/* Plan items carry a suggested service date; catalog items don't. */}
                {item.nextServiceDate ? (
                  <Typography.Text size={rpx(12)} color={LD.mist} mt={rpx(3)}>
                    建议 {item.nextServiceDate}
                  </Typography.Text>
                ) : null}
              </View>
              <DecisionChip
                decision={item.customerDecision}
                terminal={terminal}
              />
            </TouchableOpacity>
          ))
        )}
      </Card>
    </>
  );
};
